import { Injectable } from '@tanbo/vue-di-plugin'
import { Http } from '@/services/http'
import { InvokeThrottle } from '@/common/decorators/invoke-throttle'
import { ParamsClean } from '@/common/decorators/params-clean'
import { Subject } from 'rxjs'

export enum RankType {
  duration = 1,
  useSoftCount = 2,
  softCount = 3
}

export const rankCardTitleI18nKey: Record<RankType, string> = {
  [RankType.duration]: 'softwareUsage.rankTerminalDuration',
  [RankType.useSoftCount]: 'softwareUsage.rankTerminalUseCount',
  [RankType.softCount]: 'softwareUsage.rankTerminalSoftCount'
}

/** 终端视图排行饼图 series.name */
export const rankCardSeriesNameI18nKey: Record<RankType, string> = {
  [RankType.duration]: 'softwareUsage.rankSeriesTerminalDuration',
  [RankType.useSoftCount]: 'softwareUsage.rankSeriesTerminalUseCount',
  [RankType.softCount]: 'softwareUsage.rankSeriesTerminalSoftUsed'
}

export interface SoftwareUsageReq {
  /** 结束时间 */
  end_time?: string
  /** 计算机|登记用户名|ip地址 */
  key_word?: string
  /** 每页多少条 */
  limit?: number
  /** 第几页 */
  page?: number
  /** 终端分组id */
  group_id?: number
  /** 开始时间 */
  start_time?: string
  /** 排序字段 */
  sort?: string
  /** 顺序 */
  asc?: boolean
  soft_hash_key?: string
}

export interface SoftwareUsageData {
  /** 计算机名 */
  computer_name: string
  /** 登记用户名 */
  user_mark: string
  /** 分组名 */
  group_name: string
  /** 外网IP */
  client_ip: string
  /** 内网IP */
  system_ip: string
  /** 兼容图表/旧排序：与今日活跃软件数含义一致时由映射填充 */
  soft_count: number
  /** 今日活跃软件数 */
  today_active_soft_count: number
  /** 与 today_active_soft_count 同义（概览 CmTable 等兼容） */
  active_soft_count?: number
  /** 安装软件数 */
  install_soft_count: number
  /** 使用软件次数 */
  use_soft_count: number
  /** 使用时长（分钟） */
  duration: number
  /** 终端id */
  device_id: string
  /** 软件版本 */
  soft_version: string
  /** 安装时间 */
  install_time: string
  system_type?: number
  login_status?: number
  /** 操作系统名称（列表展示） */
  system_name?: string
}

export interface SoftwareUsageRes {
  list: SoftwareUsageData[] // 终端软件信息
  total: number // 总数
}

/** `/softmgr/userecord/terminal-list` 单行映射为列表展示行 */
export function mapUserecordTerminalListRow (raw: Record<string, unknown>): SoftwareUsageData {
  const todayActive = Number(
    raw.today_active_soft_count ?? raw.today_active_count ?? raw.active_soft_count ?? raw.soft_count ?? 0
  )
  const installSoft = Number(
    raw.install_soft_count ?? raw.install_total_count ?? raw.installed_soft_count ?? 0
  )
  return {
    computer_name: String(raw.computer_name ?? ''),
    user_mark: String(raw.user_mark ?? ''),
    group_name: String(raw.group_name ?? ''),
    client_ip: String(raw.client_ip ?? ''),
    system_ip: String(raw.system_ip ?? ''),
    soft_count: todayActive,
    today_active_soft_count: todayActive,
    active_soft_count: todayActive,
    install_soft_count: installSoft,
    use_soft_count: Number(raw.use_soft_count ?? raw.avg_use_count ?? 0),
    duration: Number(raw.duration ?? raw.avg_duration ?? 0),
    device_id: String(raw.device_id ?? ''),
    soft_version: String(raw.soft_version ?? ''),
    install_time: raw.install_time != null ? String(raw.install_time) : '',
    system_type: raw.system_type != null ? Number(raw.system_type) : undefined,
    login_status: raw.login_status != null ? Number(raw.login_status) : undefined,
    system_name: raw.system_name != null ? String(raw.system_name) : undefined,
  }
}

@Injectable()
export class TerminalViewService {
  updateEvent = new Subject()

  constructor (private http: Http) {
  }

  @ParamsClean()
  @InvokeThrottle()

  async getSoftwareUsage (data: SoftwareUsageReq): Promise<SoftwareUsageRes> {
    const res = await this.http.post<{ list: Record<string, unknown>[]; total: number }>(
      '/softmgr/userecord/terminal-list',
      data
    )
    return {
      list: (res.list || []).map(mapUserecordTerminalListRow),
      total: res.total ?? 0,
    }
  }
  @ParamsClean([0], 1)
  download (type: 0|1, options: SoftwareUsageReq, moduleName: string) {
    const params = Object.assign({}, options, {
      file_type: type,
      page: 0, // 不分页
      limit: 0
    })
    return this.http.downloadWithBlob('/softmgr/userecord/terminal-export', params, moduleName)
  }
}
